import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { DocumentReportIcon, UploadIcon } from "@heroicons/react/solid";
import useMountStatus from "../../../../hooks/useMountStatus";
import mime from "mime";

import { NO_ACCESS_TYPES, PERMISSIONS } from "../../../../utils/constants";
import { getReports, downloadReport } from "../../../../utils/api";

import Alert, { ALERT_TYPE } from "../../../Auth/Alert";
import ReportRow from "./ReportRow";
import DashboardContainer from "../../../SharedComponents/DashboardContainer";
import AccessControl from "../../../SharedComponents/AccessControl";
import PaginatedSearch from "../../../SharedComponents/PaginatedSearch";

function Reports() {
    const { searchTerm } = useParams();

    const [alert, setAlert] = useState("");
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const [initialReports, setInitialReports] = useState([]);
    const [reports, setReports] = useState(initialReports);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [uploadedReport, setUploadedReport] = useState("");
    const [currentlyDownloading, setCurrentlyDownloading] = useState(false);

    const unmounted = useMountStatus();

    const location = useLocation();

    const navigate = useNavigate();

    const fetchReports = useCallback(async () => {
        setReportsLoading(true);
        try {
            const reportsData = await getReports();
            if (unmounted) return;
            setInitialReports(reportsData);
            setReports(reportsData);
        } catch (err) {
            if (unmounted) return;
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setReportsLoading(false);
    }, [unmounted]);

    const handleGoToUploadReport = () => {
        navigate("/reports/upload-report");
    };

    useEffect(() => {
        fetchReports();
        location.state && location.state.uploadedReport && setUploadedReport(location.state.uploadedReport);
    }, [fetchReports, location]);

    const handleDownloadReport = async (documentIdentifier) => {
        setCurrentlyDownloading(documentIdentifier);

        const documentResponse = await downloadReport(documentIdentifier);

        const { fileType } = documentResponse;

        if (!fileType) {
            setAlertType(ALERT_TYPE.ERROR);
            setAlert("Sorry there was an error on our side, try again later or contact techsupport@psychpress.com.au");
            return;
        }
        const mimeType = mime.getType(fileType);

        if (mimeType === "false") {
            setAlertType(ALERT_TYPE.ERROR);
            setAlert("Sorry there was an error on our side, try again later or contact techsupport@psychpress.com.au");
            return;
        }
        const blob = await (await fetch(`data:${mimeType};base64,${documentResponse.content}`)).blob();

        const downloadLink = document.createElement("a");
        downloadLink.href = URL.createObjectURL(blob, { type: mimeType });
        downloadLink.download = documentResponse.fileName.split(".").join("_");
        downloadLink.click();

        setCurrentlyDownloading(false);
    };

    return (
        <AccessControl requiredPermission={PERMISSIONS.VIEW_REPORTS} noAccessType={NO_ACCESS_TYPES.REDIRECT}>
            <DashboardContainer
                title={"Reports"}
                titleIcon={<DocumentReportIcon className="block h-5 w-5 mr-2" />}
                actionButton
                actionButtonText={"Upload Report"}
                actionButtonIcon={<UploadIcon className="block h-4 w-4 mr-2" />}
                actionButtonOnClick={handleGoToUploadReport}
                actionButtonPermission={PERMISSIONS.CREATE_USER_ACCOUNTS}
            >
                <Alert text={alert} type={alertType} active={alert} close={() => setAlert("")} />
                <PaginatedSearch
                    loading={reportsLoading}
                    items={reports}
                    itemName={"Reports"}
                    searchAttributes={["name", "userName", "userEmail", "fileFormat"]}
                    columnNames={[
                        "Report",
                        "Candidate",
                        "Version",
                        "Details",
                        "Reviewing/release",
                        "Download",
                        "Manage",
                    ]}
                    ItemRow={ReportRow}
                    customItemsPerPage={16}
                    functionArray={{
                        uploadedReport: uploadedReport,
                        currentlyDownloading: currentlyDownloading,
                        handleDownloadReport: handleDownloadReport,
                    }}
                    initialSearchValue={searchTerm}
                />
            </DashboardContainer>
        </AccessControl>
    );
}

export default Reports;
